import React, { createContext, useContext, useEffect, useState } from "react";
import axios from "axios";
import { userConfirmAction, userSuccess } from "../utils/toastUtils";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const API = process.env.REACT_APP_API_URL || "";
  
  useEffect(() => {
    const storedToken = localStorage.getItem("userToken");
    const storedUser = localStorage.getItem("userData");

    if (storedToken && storedUser) {
      setToken(storedToken);
      setUser(JSON.parse(storedUser));
    }

    setLoading(false);
  }, []);

  const login = async ({ identifier, password }) => {
    const res = await axios.post(
      `${process.env.REACT_APP_API_URL}/auth/login`,
      { identifier, password },
    );

    const { token, user } = res.data;

    localStorage.setItem("userToken", token);
    localStorage.setItem("userData", JSON.stringify(user));

    setToken(token);
    setUser(user);

    userSuccess(res?.data?.message);
    return user;
  };

  const register = async (payload) => {
    // console.log("payload:", payload);
    const res = await axios.post(
      `${process.env.REACT_APP_API_URL}/auth/register`,
      payload,
    );
    userSuccess(res?.data?.message);
  };

  const forgotPassword = async ({ email, phoneNumber, newPassword }) => {
    const res = await axios.post(
      `${process.env.REACT_APP_API_URL}/auth/forgot-password`,
      { email, phoneNumber, newPassword },
    );
    userSuccess(res?.data?.message);
  };

  const persistAuth = (token, user) => {
    localStorage.setItem("userToken", token);
    localStorage.setItem("userData", JSON.stringify(user));
    setToken(token);
    setUser(user);
  };

  const requestOtp = async (phoneNumber) => {
    const res = await axios.post(`${API}/auth/request-otp`, {
      phoneNumber,
    });
    return res.data; // { expiresIn }
  };

  const resendOtp = async (phoneNumber) => {
    const res = await axios.post(`${API}/auth/resend-otp`, {
      phoneNumber,
    });
    return res.data; // { expiresIn }
  };

  const verifyOtp = async ({ phoneNumber, otp }) => {
    const res = await axios.post(`${API}/auth/verify-otp`, {
      phoneNumber,
      otp,
    });

    // existing user → auto login
    if (res.data.isRegistered) {
      persistAuth(res.data.token, res.data.user);
      userSuccess("Login successful");
    }

    return res.data;
    // { isRegistered, token?, user? }
  };

  const completeRegistration = async (payload) => {
    const res = await axios.post(`${API}/auth/complete-registration`, payload);

    persistAuth(res.data.token, res.data.user);
    userSuccess("Account created successfully");
    return res.data.user;
  };

  const logout = async () => {
    const confirmed = await userConfirmAction({
      title: "Log out?",
      text: "You’ll be signed out of your account on this device.",
      confirmText: "Yes, log out",
      cancelText: "Stay logged in",
      icon: "warning",
    });

    if (!confirmed) return;

    localStorage.removeItem("userToken");
    localStorage.removeItem("userData");
    setToken(null);
    setUser(null);

    userSuccess("You’ve been logged out successfully");
  };

  const value = {
    user,
    token,
    isAuthenticated: !!token,
    register,
    login,
    forgotPassword,

    requestOtp,
    resendOtp,
    verifyOtp,
    completeRegistration,

    logout,
    setUser,
  };

  if (loading) return null;

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  return useContext(AuthContext);
};
